import { createSlice } from '@reduxjs/toolkit';
import { isMobileDevice } from 'utils';
import { notificationsData } from 'data/notificationData';

const initialUnreadCount = Array.isArray(notificationsData)
  ? notificationsData.filter((n) => !n.isRead).length
  : 0;

/**
 * Global Slice
 *
 * Manages global UI-related state that is not tied to specific features.
 * This includes global UI toggles, modals, and visual preferences.
 */
const initialState = {
  availableModels: [],
  showGigabot: !isMobileDevice(),
  notificationVolume: 50,
  notificationMuted: false,
  notifications: Array.isArray(notificationsData) ? notificationsData : [],
  notificationCount: initialUnreadCount,
  enabledFeatures: {
    action_proposals: false,
    web_search: false,
    google_auth: false,
    signup: false,
  },
};

const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setShowGigabot: (state, action) => {
      state.showGigabot = action.payload;
    },
    toggleGigabot: (state) => {
      state.showGigabot = !state.showGigabot;
    },
    setNotificationVolume: (state, action) => {
      state.notificationVolume = action.payload;
    },
    setNotificationMuted: (state, action) => {
      state.notificationMuted = action.payload;
    },
    setAvailableModels: (state, action) => {
      state.availableModels = action.payload;
    },
    setEnabledFeatures: (state, action) => {
      state.enabledFeatures = action.payload;
    },
    setNotificationCount: (state, action) => {
      state.notificationCount = action.payload;
    },
    incrementNotificationCount: (state) => {
      state.notificationCount += 1;
    },
    addNotification: (state, action) => {
      const exists = state.notifications.some(
        (n) => String(n.id) === String(action.payload.id)
      );
      if (exists) return;

      const isRead = action.payload.isRead ?? false;
      state.notifications = [
        { ...action.payload, isRead },
        ...state.notifications,
      ];
      if (!isRead) {
        state.notificationCount += 1;
      }
    },
    markNotificationAsRead: (state, action) => {
      const targetId = String(action.payload);
      let wasUnread = false;
      state.notifications = state.notifications.map((n) => {
        if (String(n.id) === targetId && !n.isRead) {
          wasUnread = true;
          return { ...n, isRead: true };
        }
        return n;
      });
      if (wasUnread && state.notificationCount > 0) {
        state.notificationCount -= 1;
      }
    },
    decrementNotificationCount: (state) => {
      if (state.notificationCount > 0) {
        state.notificationCount -= 1;
      }
    },
  },
});

export const {
  setShowGigabot,
  toggleGigabot,
  setNotificationVolume,
  setNotificationMuted,
  setNotificationCount,
  incrementNotificationCount,
  decrementNotificationCount,
  addNotification,
  markNotificationAsRead,
  setAvailableModels,
  setEnabledFeatures,
} = globalSlice.actions;
export default globalSlice.reducer;
